// CL-234 — Invio di un messaggio del salone ai propri clienti.
//
// Un solo modal, due punti d'ingresso: la pagina Clienti (esperienza completa) e
// la toolbar dell'agenda (precompilato con la data visualizzata). Le due entrate
// condividono lo stesso componente, così i filtri si comportano allo stesso modo.
//
// Il conteggio dei destinatari arriva sempre dal server (RPC get_broadcast_recipients),
// mai da un calcolo locale: il numero scritto sul bottone è esattamente quello che parte.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
    AlertTriangle,
    Bell,
    Calendar as CalendarIcon,
    Check,
    ChevronDown,
    Clock,
    Loader2,
    Mail,
    Search,
    Send,
    Users,
} from 'lucide-react';

import Modal from '../ui/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import DatePicker from '../ui/DatePicker';
import PrimaryButton from '../ui/PrimaryButton';

import { useAuth } from '@/contexts/AuthContext';
import {
    BroadcastFilter,
    BroadcastMode,
    BroadcastRecipient,
    MAX_BODY_LENGTH,
    MAX_TITLE_LENGTH,
    countTargets,
    describeTimeRange,
    fetchBroadcastRecipients,
    sendBroadcast,
    summarizeRecipients,
    validateBroadcastMessage,
    validateTimeRange,
} from '@/services/notifications/broadcastService';

export interface SendNotificationPreset {
    mode?: BroadcastMode;
    date?: Date;
    from?: string;
    to?: string;
}

interface SendNotificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Precompilazione dall'agenda: data (ed eventuale fascia oraria) già impostate. */
    preset?: SendNotificationPreset;
}

const SendNotificationModal: React.FC<SendNotificationModalProps> = ({ isOpen, onClose, preset }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { organization } = useAuth();
    const shopId = organization?.id;

    // --- Filtro destinatari ---------------------------------------------------
    // Due sole sezioni: "Per data" e "Scegli clienti". "Tutti" non è una sezione
    // a sé, è la prima voce della lista clienti — così scegliere fra tutti e
    // qualcuno resta un gesto solo, dentro allo stesso elenco.
    const [tab, setTab] = useState<'date' | 'customers'>('customers');
    // Nessuno selezionato all'apertura: scrivere a tutta la rubrica dev'essere
    // un gesto deliberato, non il default che parte se ci si distrae.
    const [allSelected, setAllSelected] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    /** L'elenco dei singoli clienti si apre solo se serve davvero. */
    const [isListOpen, setIsListOpen] = useState(false);
    /** Fallback: chi non ha l'app ma ha un indirizzo riceve un'email. */
    const [sendEmail, setSendEmail] = useState(false);
    const [search, setSearch] = useState('');
    const [date, setDate] = useState<Date | undefined>(undefined);
    // Estremi facoltativi: vuoti = tutta la giornata, solo uno = aperto da quel lato.
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    // --- Messaggio ------------------------------------------------------------
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');

    // --- Dati e stato ---------------------------------------------------------
    /** Rubrica completa del salone con la raggiungibilità: caricata una volta sola. */
    const [roster, setRoster] = useState<BroadcastRecipient[]>([]);
    const [dateRecipients, setDateRecipients] = useState<BroadcastRecipient[]>([]);
    const [isLoadingRoster, setIsLoadingRoster] = useState(false);
    const [isLoadingDate, setIsLoadingDate] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [showUnreachable, setShowUnreachable] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    // Il filtro mandato al server: "tutti" e "scelti a mano" restano due modalità
    // distinte anche se nella UI vivono nella stessa sezione, così lo storico
    // registra "inviata a tutti" invece di una lista di 261 id.
    const mode: BroadcastMode = tab === 'date' ? 'date' : allSelected ? 'all' : 'selected';

    // Il preset arriva dal chiamante e può essere ricreato a ogni render: lo teniamo
    // in un ref così il form si azzera SOLO all'apertura, non a ogni render del padre.
    const presetRef = useRef(preset);
    presetRef.current = preset;

    // All'apertura: azzera il form e applica l'eventuale preset dell'agenda.
    useEffect(() => {
        if (!isOpen) return;
        const p = presetRef.current;
        setTab(p?.mode === 'date' ? 'date' : 'customers');
        setAllSelected(false);
        setSelectedIds([]);
        setIsListOpen(false);
        setSendEmail(false);
        setSearch('');
        setDate(p?.date);
        setFrom(p?.from ?? '');
        setTo(p?.to ?? '');
        setTitle('');
        setBody('');
        setShowUnreachable(false);
        setIsConfirmOpen(false);
        setDateRecipients([]);
    }, [isOpen]);

    // Rubrica completa: serve sia per "tutti" sia per la lista di selezione.
    useEffect(() => {
        if (!isOpen || !shopId) return;
        let cancelled = false;

        setIsLoadingRoster(true);
        fetchBroadcastRecipients(shopId, { mode: 'all' })
            .then((data) => { if (!cancelled) setRoster(data); })
            .catch((e) => {
                if (!cancelled) toast.error(e?.message || t('broadcast.load_error', 'Impossibile caricare i clienti'));
            })
            .finally(() => { if (!cancelled) setIsLoadingRoster(false); });

        return () => { cancelled = true; };
    }, [isOpen, shopId, t]);

    const dateKey = date ? format(date, 'yyyy-MM-dd') : null;
    const timeRangeValid = validateTimeRange(from, to).isValid;

    // Chi ha prenotato in quel giorno (ed eventuale fascia): solo il server lo sa.
    useEffect(() => {
        // Filtro incompleto o modalità diversa: nessun destinatario "per data" da mostrare.
        // Va azzerato, altrimenti il riepilogo continuerebbe a contare la ricerca precedente.
        if (!isOpen || !shopId || mode !== 'date' || !dateKey || !timeRangeValid) {
            setIsLoadingDate(false);
            setDateRecipients((prev) => (prev.length === 0 ? prev : []));
            return;
        }
        let cancelled = false;

        setIsLoadingDate(true);
        const timer = setTimeout(() => {
            fetchBroadcastRecipients(shopId, {
                mode: 'date',
                date: dateKey,
                from: from || null,
                to: to || null,
            })
                .then((data) => { if (!cancelled) setDateRecipients(data); })
                .catch((e) => {
                    if (!cancelled) toast.error(e?.message || t('broadcast.load_error', 'Impossibile caricare i clienti'));
                })
                .finally(() => { if (!cancelled) setIsLoadingDate(false); });
        }, 250);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [isOpen, shopId, mode, dateKey, from, to, timeRangeValid, t]);

    // --- Destinatari correnti -------------------------------------------------
    const currentRecipients = useMemo<BroadcastRecipient[]>(() => {
        if (mode === 'all') return roster;
        if (mode === 'selected') return roster.filter((r) => selectedIds.includes(r.customer_id));
        return dateRecipients;
    }, [mode, roster, selectedIds, dateRecipients]);

    const summary = useMemo(() => summarizeRecipients(currentRecipients), [currentRecipients]);
    /** Quante persone riceveranno davvero il messaggio, col flag email com'è ora. */
    const targetCount = countTargets(summary, sendEmail);
    /** Chi resta fuori: senza app, e senza email o col fallback spento. */
    const excludedList = useMemo(
        () => currentRecipients.filter((r) => !r.reachable && (!sendEmail || !r.email)),
        [currentRecipients, sendEmail],
    );

    const filteredRoster = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return roster;
        return roster.filter(
            (r) =>
                (r.full_name || '').toLowerCase().includes(term) ||
                (r.phone || '').toLowerCase().includes(term),
        );
    }, [roster, search]);

    const isLoading = isLoadingRoster || (mode === 'date' && isLoadingDate);
    const messageValidation = validateBroadcastMessage(title, body);
    const canSend =
        !isLoading &&
        !isSending &&
        targetCount > 0 &&
        messageValidation.isValid &&
        (mode !== 'date' || (Boolean(dateKey) && timeRangeValid)) &&
        (mode !== 'selected' || selectedIds.length > 0);

    /** Chi si può spuntare: ha l'app oppure almeno un indirizzo email. */
    const selectableIds = useMemo(
        () => roster.filter((r) => r.reachable || r.email).map((r) => r.customer_id),
        [roster],
    );
    const rosterSummary = useMemo(() => summarizeRecipients(roster), [roster]);

    const isCustomerChecked = (id: string) => allSelected || selectedIds.includes(id);

    const toggleCustomer = (id: string) => {
        // Togliere una spunta mentre è attivo "tutti" non azzera la scelta:
        // si passa a una selezione puntuale con tutti gli altri già dentro.
        if (allSelected) {
            setAllSelected(false);
            setSelectedIds(selectableIds.filter((x) => x !== id));
            return;
        }
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const toggleAll = () => {
        setAllSelected((prev) => !prev);
        setSelectedIds([]);
    };

    const buildFilter = (): BroadcastFilter => ({
        mode,
        customerIds: selectedIds,
        date: dateKey,
        from: from || null,
        to: to || null,
    });

    const handleSend = async () => {
        if (!shopId || !canSend) return;
        setIsSending(true);
        try {
            const result = await sendBroadcast(shopId, buildFilter(), title, body, sendEmail);
            const emails = result.emails ?? 0;
            toast.success(
                emails > 0
                    ? t('broadcast.sent_with_email', 'Inviata a {{count}} clienti sull\'app e a {{emails}} via email', {
                        count: result.sent,
                        emails,
                    })
                    : t('broadcast.sent', 'Notifica inviata a {{count}} clienti', { count: result.sent }),
            );
            if (result.emails_failed) {
                toast.error(
                    t('broadcast.emails_failed', '{{count}} email non sono partite', { count: result.emails_failed }),
                );
            }
            setIsConfirmOpen(false);
            onClose();
        } catch (e: any) {
            toast.error(e?.message || t('broadcast.send_error', "Invio non riuscito"));
        } finally {
            setIsSending(false);
        }
    };

    // --- Sotto-componenti locali ---------------------------------------------
    const tabButton = (value: 'date' | 'customers', label: string) => (
        <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                tab === value ? 'bg-white text-clip-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
        >
            {label}
        </button>
    );

    const sectionLabel = (text: string) => (
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">{text}</p>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                compact
                closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
                headerContent={
                    <div className="flex items-center gap-3 pr-10">
                        <div className="w-11 h-11 rounded-2xl bg-clip-50 flex items-center justify-center flex-shrink-0">
                            <Bell size={22} className="text-clip-500" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-bold text-gray-900 truncate">
                                {t('broadcast.title', 'Invia notifica ai clienti')}
                            </h2>
                            <p className="text-xs text-gray-500">
                                {t('broadcast.subtitle', "Arriva come notifica sul telefono e nell'app.")}
                            </p>
                        </div>
                    </div>
                }
            >
                <div className="space-y-5">
                    {/* ---------------- DESTINATARI ---------------- */}
                    <div>
                        {sectionLabel(t('broadcast.recipients', 'Destinatari'))}
                        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                            {tabButton('date', t('broadcast.mode_date', 'Per data'))}
                            {tabButton('customers', t('broadcast.mode_selected', 'Scegli clienti'))}
                        </div>

                        {/* Elenco clienti: "Tutti" è la prima voce, non una sezione separata */}
                        {tab === 'customers' && (
                            <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
                                {/* Voce "Tutti i clienti": sempre in cima, mai filtrata dalla ricerca */}
                                <button
                                    type="button"
                                    onClick={toggleAll}
                                    className={`w-full flex items-center gap-3 px-3 py-3 text-left border-b transition-colors ${
                                        allSelected
                                            ? 'bg-clip-50/60 border-clip-100 hover:bg-clip-50'
                                            : 'border-gray-100 hover:bg-gray-50'
                                    }`}
                                >
                                    <span
                                        className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                                            allSelected ? 'bg-clip-500 border-clip-500' : 'bg-white border-gray-300'
                                        }`}
                                    >
                                        {allSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className={`block text-sm font-bold ${allSelected ? 'text-clip-700' : 'text-gray-800'}`}>
                                            {t('broadcast.all_customers', 'Tutti i clienti')}
                                        </span>
                                        <span className="block text-[11px] text-gray-400">
                                            {t('broadcast.all_customers_hint', '{{count}} sull\'app, {{email}} solo via email, su {{total}}', {
                                                count: rosterSummary.reachable,
                                                email: rosterSummary.emailable,
                                                total: rosterSummary.total,
                                            })}
                                        </span>
                                    </span>
                                </button>

                                {/* L'elenco dei singoli clienti resta chiuso finché non serve */}
                                <button
                                    type="button"
                                    onClick={() => setIsListOpen((v) => !v)}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                >
                                    <ChevronDown
                                        size={15}
                                        className={`text-gray-400 flex-shrink-0 transition-transform ${isListOpen ? 'rotate-180' : ''}`}
                                    />
                                    <span className="flex-1 text-sm font-medium text-gray-600">
                                        {t('broadcast.pick_some', 'Scegli singoli clienti')}
                                    </span>
                                    {!allSelected && selectedIds.length > 0 && (
                                        <span className="text-[11px] font-bold text-clip-600 flex-shrink-0">
                                            {t('broadcast.selected_count', '{{count}} selezionati', { count: selectedIds.length })}
                                        </span>
                                    )}
                                </button>

                                {isListOpen && (
                                    <>
                                <div className="relative border-y border-gray-100">
                                    <Search
                                        size={16}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                    />
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={t('broadcast.search_placeholder', 'Cerca per nome o telefono') as string}
                                        className="w-full pl-9 pr-3 py-2.5 text-sm focus:outline-none placeholder:text-gray-400"
                                    />
                                </div>

                                <div className="max-h-56 overflow-y-auto custom-scrollbar">
                                    {isLoadingRoster ? (
                                        <div className="p-6 flex justify-center">
                                            <Loader2 size={18} className="animate-spin text-gray-400" />
                                        </div>
                                    ) : filteredRoster.length === 0 ? (
                                        <p className="p-6 text-center text-sm text-gray-400">
                                            {t('broadcast.no_customers', 'Nessun cliente trovato.')}
                                        </p>
                                    ) : (
                                        filteredRoster.map((r) => {
                                            const checked = isCustomerChecked(r.customer_id);
                                            // Selezionabile se ha l'app o almeno un indirizzo email.
                                            const selectable = r.reachable || Boolean(r.email);
                                            return (
                                                <button
                                                    key={r.customer_id}
                                                    type="button"
                                                    disabled={!selectable}
                                                    onClick={() => toggleCustomer(r.customer_id)}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-gray-50 last:border-0 transition-colors ${
                                                        selectable
                                                            ? 'hover:bg-gray-50 cursor-pointer'
                                                            : 'opacity-50 cursor-not-allowed'
                                                    }`}
                                                >
                                                    <span
                                                        className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                                                            checked
                                                                ? 'bg-clip-500 border-clip-500'
                                                                : 'bg-white border-gray-300'
                                                        }`}
                                                    >
                                                        {checked && <Check size={12} className="text-white" strokeWidth={3} />}
                                                    </span>
                                                    <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-800">
                                                        {r.full_name}
                                                    </span>
                                                    {!r.reachable && (
                                                        <span
                                                            className={`text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${
                                                                r.email && sendEmail ? 'text-clip-500' : 'text-gray-400'
                                                            }`}
                                                        >
                                                            {r.email
                                                                ? t('broadcast.via_email', 'via email')
                                                                : t('broadcast.no_contact', 'non raggiungibile')}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Chi ha prenotato in una data */}
                        {tab === 'date' && (
                            <div className="mt-3 space-y-3">
                                <div className="flex items-center gap-2">
                                    <CalendarIcon size={16} className="text-gray-400 flex-shrink-0" />
                                    <DatePicker
                                        selected={date}
                                        onSelect={setDate}
                                        allowClosedDays
                                        placeholder={t('broadcast.pick_date', 'Scegli la data') as string}
                                        className="flex-1"
                                    />
                                </div>

                                {/* Fascia oraria: entrambi gli estremi facoltativi.
                                    Vuoti = tutta la giornata; uno solo = intervallo aperto da quel lato. */}
                                <div className="flex items-center flex-wrap gap-2">
                                    <Clock size={16} className="text-gray-400 flex-shrink-0" />
                                    <input
                                        type="time"
                                        step={900}
                                        value={from}
                                        onChange={(e) => setFrom(e.target.value)}
                                        aria-label={t('broadcast.time_from', 'Dalle') as string}
                                        className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-clip-200 focus:border-clip-400"
                                    />
                                    <span className="text-gray-400 text-sm">–</span>
                                    <input
                                        type="time"
                                        step={900}
                                        value={to}
                                        onChange={(e) => setTo(e.target.value)}
                                        aria-label={t('broadcast.time_to', 'Alle') as string}
                                        className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-clip-200 focus:border-clip-400"
                                    />
                                    {(from || to) && (
                                        <button
                                            type="button"
                                            onClick={() => { setFrom(''); setTo(''); }}
                                            className="text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors px-1"
                                        >
                                            {t('broadcast.clear_time', 'Azzera')}
                                        </button>
                                    )}
                                </div>

                                {timeRangeValid ? (
                                    <p className="text-xs text-gray-400 pl-6">
                                        {describeTimeRange(from, to)}
                                    </p>
                                ) : (
                                    <p className="text-xs text-red-500 pl-6">
                                        {validateTimeRange(from, to).message}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Fallback email: solo per chi non riceverebbe la push */}
                        <label
                            className={`mt-3 flex items-start gap-2.5 p-3 rounded-xl border transition-colors ${
                                summary.emailable === 0
                                    ? 'border-gray-100 bg-gray-50/50 cursor-not-allowed'
                                    : sendEmail
                                        ? 'border-clip-200 bg-clip-50/50 cursor-pointer'
                                        : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={sendEmail}
                                disabled={summary.emailable === 0}
                                onChange={(e) => setSendEmail(e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-clip-500 focus:ring-clip-400 disabled:opacity-40"
                            />
                            <span className="min-w-0">
                                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                                    <Mail size={14} className={sendEmail ? 'text-clip-500' : 'text-gray-400'} />
                                    {t('broadcast.send_email', "Invia email ai clienti senza app")}
                                </span>
                                <span className="block text-[11px] text-gray-400 mt-0.5">
                                    {summary.emailable === 0
                                        ? t('broadcast.send_email_none', "Nessuno dei destinatari senza app ha un indirizzo email.")
                                        : t('broadcast.send_email_hint', '{{count}} destinatari senza app hanno un indirizzo email.', {
                                            count: summary.emailable,
                                        })}
                                </span>
                            </span>
                        </label>
                    </div>

                    {/* ---------------- MESSAGGIO ---------------- */}
                    <div>
                        {sectionLabel(t('broadcast.message', 'Messaggio'))}
                        <div className="space-y-2">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={title}
                                    maxLength={MAX_TITLE_LENGTH}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t('broadcast.title_placeholder', 'Titolo (es. Chiusura anticipata)') as string}
                                    className="w-full px-4 py-2.5 pr-14 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-clip-200 focus:border-clip-400 placeholder:text-gray-400"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 tabular-nums">
                                    {title.length}/{MAX_TITLE_LENGTH}
                                </span>
                            </div>

                            <div className="relative">
                                <textarea
                                    value={body}
                                    maxLength={MAX_BODY_LENGTH}
                                    rows={3}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder={t('broadcast.body_placeholder', 'Testo del messaggio…') as string}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-clip-200 focus:border-clip-400 placeholder:text-gray-400"
                                />
                                <span className="absolute right-3 bottom-3 text-[10px] text-gray-400 tabular-nums">
                                    {body.length}/{MAX_BODY_LENGTH}
                                </span>
                            </div>
                        </div>

                        {/* Anteprima: com'è che la vedrà il cliente */}
                        {(title || body) && (
                            <div className="mt-3">
                                <p className="text-[10px] font-bold text-gray-300 uppercase tracking-wider mb-1.5">
                                    {t('broadcast.preview', 'Anteprima')}
                                </p>
                                <div className="rounded-2xl bg-gray-900 p-3.5 shadow-sm">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Bell size={11} className="text-gray-400" />
                                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide truncate">
                                            {organization?.name || 'Clip'}
                                        </span>
                                    </div>
                                    <p className="text-sm font-bold text-white break-words">
                                        {title || t('broadcast.title_placeholder_short', 'Titolo')}
                                    </p>
                                    <p className="text-xs text-gray-300 mt-0.5 break-words">
                                        {body || t('broadcast.body_placeholder_short', 'Testo del messaggio')}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ---------------- RIEPILOGO DESTINATARI ---------------- */}
                    <div
                        className={`rounded-2xl border p-4 ${
                            targetCount > 0
                                ? 'bg-gray-50 border-gray-100'
                                : 'bg-amber-50 border-amber-200'
                        }`}
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                <Loader2 size={16} className="animate-spin" />
                                {t('broadcast.counting', 'Calcolo dei destinatari…')}
                            </div>
                        ) : targetCount > 0 ? (
                            <>
                                <p className="flex items-center gap-2 text-sm text-gray-700">
                                    <Users size={16} className="text-clip-500 flex-shrink-0" />
                                    <span>
                                        <strong className="font-bold text-gray-900">{targetCount}</strong>{' '}
                                        {t('broadcast.will_receive', 'clienti riceveranno la notifica')}
                                    </span>
                                </p>
                                {sendEmail && summary.emailable > 0 && (
                                    <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 pl-[22px]">
                                        {t('broadcast.split_hint', '{{app}} sull\'app, {{email}} via email', {
                                            app: summary.reachable,
                                            email: summary.emailable,
                                        })}
                                    </p>
                                )}
                                {excludedList.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowUnreachable((v) => !v)}
                                        className="mt-1.5 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                    >
                                        <ChevronDown
                                            size={13}
                                            className={`transition-transform ${showUnreachable ? 'rotate-180' : ''}`}
                                        />
                                        {t('broadcast.excluded_count', '{{count}} resteranno esclusi', {
                                            count: excludedList.length,
                                        })}
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="flex gap-2.5">
                                <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-amber-800">
                                    <p className="font-bold">
                                        {mode === 'selected' && selectedIds.length === 0
                                            ? t('broadcast.none_picked', 'Nessun cliente selezionato')
                                            : t('broadcast.none_reachable', 'Nessun destinatario raggiungibile')}
                                    </p>
                                    <p className="text-xs mt-0.5 text-amber-700">
                                        {mode === 'selected' && selectedIds.length === 0
                                            ? t('broadcast.none_picked_hint', 'Spunta "Tutti i clienti" oppure scegli chi vuoi avvisare dall\'elenco.')
                                            : summary.total === 0
                                                ? t('broadcast.none_matching', 'Nessun cliente corrisponde a questo filtro.')
                                                : summary.emailable > 0
                                                    ? t('broadcast.none_but_email', "Nessuno dei {{count}} clienti selezionati ha l'app, ma {{email}} hanno un indirizzo email: attiva l'invio via email qui sopra.", { count: summary.total, email: summary.emailable })
                                                    : t('broadcast.none_with_app', "Nessuno dei {{count}} clienti selezionati ha l'app o un indirizzo email: non c'è modo di recapitare il messaggio.", { count: summary.total })}
                                    </p>
                                    {summary.total > 0 && shopId && (
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/download/${shopId}`)}
                                            className="mt-2 text-xs font-bold text-amber-900 underline underline-offset-2 hover:text-amber-950"
                                        >
                                            {t('broadcast.invite_to_download', "Invita i clienti a scaricare l'app")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {showUnreachable && excludedList.length > 0 && (
                            <div className="mt-2 max-h-32 overflow-y-auto custom-scrollbar border-t border-gray-200 pt-2">
                                {excludedList.map((r) => (
                                    <p key={r.customer_id} className="text-xs text-gray-400 py-0.5 truncate">
                                        {r.full_name}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ---------------- AZIONI ---------------- */}
                    <div className="flex items-center justify-end gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 rounded-xl font-bold text-sm text-gray-500 hover:bg-gray-100 transition-colors"
                        >
                            {t('common.cancel', 'Annulla')}
                        </button>
                        <PrimaryButton
                            icon={Send}
                            showLabelOnMobile
                            disabled={!canSend}
                            onClick={() => setIsConfirmOpen(true)}
                            label={
                                targetCount > 0
                                    ? t('broadcast.send_to', 'Invia a {{count}} clienti', { count: targetCount })
                                    : t('broadcast.send', 'Invia')
                            }
                        />
                    </div>
                </div>
            </Modal>

            {/* L'invio è irreversibile: seconda conferma esplicita. */}
            <ConfirmDialog
                isOpen={isConfirmOpen}
                variant="default"
                loading={isSending}
                title={t('broadcast.confirm_title', 'Confermi l’invio?')}
                description={
                    sendEmail && summary.emailable > 0
                        ? t(
                            'broadcast.confirm_description_email',
                            'Stai per inviare "{{title}}" a {{count}} clienti: {{app}} sull\'app e {{email}} via email. Non si può annullare.',
                            {
                                title: title.trim(),
                                count: targetCount,
                                app: summary.reachable,
                                email: summary.emailable,
                            },
                        )
                        : t(
                            'broadcast.confirm_description',
                            'Stai per inviare "{{title}}" a {{count}} clienti. La notifica non può essere annullata.',
                            { title: title.trim(), count: targetCount },
                        )
                }
                confirmLabel={t('broadcast.confirm_send', 'Invia ora')}
                onConfirm={handleSend}
                onCancel={() => setIsConfirmOpen(false)}
            />
        </>
    );
};

export default SendNotificationModal;
